import { useNode } from "@craftjs/core";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { RowBlockSettings } from "@/components/craft/settings/RowBlockSettings";

type RowBlockProps = {
  gap?: number;
  align?: "start" | "center" | "end" | "stretch";
  justify?: "start" | "center" | "end" | "between";
  wrap?: boolean;
  style?: ResponsiveStyleProps;
  children?: ReactNode;
};

const alignMap = { start: "flex-start", center: "center", end: "flex-end", stretch: "stretch" } as const;
const justifyMap = { start: "flex-start", center: "center", end: "flex-end", between: "space-between" } as const;

export function RowBlock({
  gap = 24,
  align = "stretch",
  justify = "start",
  wrap = true,
  style = defaultStyleProps(),
  children,
}: RowBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));

  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn(
        "craft-node",
        `craft-node-${id}`,
        styleClass,
        selected && "outline outline-2 outline-dashed outline-primary/25",
      )}
      style={{
        ...inlineStyle,
        display: "flex",
        flexWrap: wrap ? "wrap" : "nowrap",
        gap: `${gap}px`,
        alignItems: alignMap[align],
        justifyContent: justifyMap[justify],
      }}
    >
      {children}
    </div>
  );
}

RowBlock.craft = {
  displayName: "Row",
  isCanvas: true,
  props: {
    gap: 24,
    align: "stretch",
    justify: "start",
    wrap: true,
    style: defaultStyleProps(),
  },
  rules: {
    canDrag: () => true,
    canMoveIn: (incoming) =>
      incoming.some((node) => node.data.name === "ColumnBlock"),
  },
  related: {
    settings: RowBlockSettings,
  },
};
